import React from 'react';
import {
  Box,
  Button,
  Column,
  Container,
  Divider,
  Flex,
  OptionType,
  Pane,
  SelectMenu,
  Toggle,
  Typography,
} from '@fullstory/components';
import { ClickData, ClickTypes, clickTypes, clickTypeToIcon, TopSelectorData } from '../../types';
import { EngagementMapsStatus } from '../../Components/EngagementMapsStatus';
import { EngagementMapsState, SegmentData } from '../../Components/types';
import { EngagementMapsDetailsCard } from '../../Components/EngagementMapsDetailsCard';
import {
  createSearchActions,
  EngagementMapsRankedCard,
  EngagementMapsRankedCardActionRowInfo,
} from '../../Components/EngagementMapsRankedCard';
import { getClickTypeColor, noop } from '../../Utils';
import { useHistory } from 'react-router-dom';
import { generateRoutePath, SETTINGS_PAGES } from '@fullstory/routes';
import { useSelector } from 'react-redux';
import { fullstorySelectors } from '@fullstory/redux';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { useFlags } from '@fullstory/react-hooks';
import { SegmentRow } from '../../Components/SegmentRow';
import { PageDefinitionRow } from '../../Components/PageDefinitionRow';
import { PagePathRow } from '../../Components/PagePathRow';
import { TimeFrameRow } from '../../Components/TimeFrameRow';
import { ClicksRow } from '../../Components/ClicksRow';
import { UsersRow } from '../../Components/UsersRow';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  HeatmapRouteSettings,
  useRouteToHeatmap,
} from '@fullstory/library-ui/hooks/useRouteToHeatmap';
import { ContactUs } from '../../Components/ContactUs';

const { getOrgId } = fullstorySelectors;

export interface ClickMapsSidebarProps {
  /**
   * The state of the toggle for whether to display the Click Maps as a Heat Map
   */
  engagementHeatMapToggleChecked: boolean;
  /**
   * A callback provided when the user wishes to toggle whether to display the Click Maps as a Heat Map
   */
  onEngagementHeatMapToggle: (checked: boolean) => void;
  /**
   * The current Click Maps type.
   */
  currentClickMapsType: ClickTypes;
  /**
   * A callback provided when the user wishes to change which Click Map type is selected.
   */
  onClickMapsTypeChange: (option: OptionType<ClickTypes> | null) => void;
  /**
   * The current state of loading the Click Maps data. This is used to determine what loading message to display.
   */
  loadingState: EngagementMapsState;
  /**
   * Information about the segment being used to create the results
   */
  segmentData: SegmentData;
  /**
   * A callback for handling navigating back to the segment definition upon click
   */
  navigateToSegment: () => void;
  /**
   * Information about the data of Click Maps itself
   */
  clickMapsData?: ClickData;
  /**
   * A number indicating which selector in the clickMapsData.topSelectorData array is currently active.
   */
  activeSelector?: number;
  /**
   * A callback to handle when a selector in the clickMapsData.topSelectorData array is clicked on. Typically, the
   * caller will update this selector as the active one.
   */
  onSelectorClick?: (rank: number) => void;
  /**
   * An optional callback for adding a selector from the clickMapsData.topSelectorData array as a search criteria to
   * the existing search.
   */
  onSelectorAddToSearch?: (rank: number) => void;
  /**
   * An optional callback for using a selector from the clickMapsData.topSelectorData array to create a new
   * search criteria.
   */
  onSelectorCreateSearch?: (rank: number) => void;
  /**
   * An optional callback for using a selector from the clickMapsData.topSelectorData array to create a new
   * defined event.
   */
  onSelectorAddDefinedEvent?: (rank: number) => void;
  /**
   * An optional callback for using a selector from the clickMapsData.topSelectorData array to create a new
   * named element.
   */
  onSelectorNameElement?: (rank: number, forceNew: boolean) => void;
  /**
   * An optional callback for using a selector from the clickMapsData.topSelectorData array to create a new
   * recording rule.
   */
  onSelectorModifyRecordingRules?: (rank: number) => void;
  /**
   * An optional callback for using a selector from the clickMapsData.topSelectorData array to create a new
   * behavior signal setting.
   */
  onSelectorIgnoreInFrustrationSignals?: (rank: number) => void;
  /**
   * Route settings for the Explore Heatmap action.
   */
  routeSettings: HeatmapRouteSettings;
}

/**
 * The Click Maps sidebar responsible for displaying information about the loading state of Click Maps.
 */
export const ClickMapsSidebar: React.FC<ClickMapsSidebarProps> = ({
  engagementHeatMapToggleChecked,
  onEngagementHeatMapToggle,
  currentClickMapsType,
  onClickMapsTypeChange,
  loadingState,
  segmentData,
  navigateToSegment,
  clickMapsData,
  activeSelector = 0,
  onSelectorClick,
  onSelectorAddToSearch,
  onSelectorCreateSearch,
  onSelectorAddDefinedEvent,
  onSelectorNameElement,
  onSelectorModifyRecordingRules,
  onSelectorIgnoreInFrustrationSignals,
  routeSettings,
}) => {
  const history = useHistory();
  const orgId = useSelector(getOrgId);
  const flags = useFlags();
  const canAccess = flags.hasBit(Flag.PermModifyPageDefinitions);
  const intl = useIntl();
  const routeToHeatmap = useRouteToHeatmap();

  function createClickMapsTypeOption(clickMapsType: ClickTypes): OptionType<ClickTypes> {
    return {
      label: intl.formatMessage(
        {
          defaultMessage:
            'Most {clickMapsType, select, error_clicked {error clicked} rage_clicked {rage clicked} dead_clicked {dead clicked} other {clicked}}',
          id: 'II1OrF',
        },
        { clickMapsType },
      ),
      value: clickMapsType,
      key: clickMapsType,
      icon: clickTypeToIcon[clickMapsType],
    };
  }

  function createActions(selector: TopSelectorData): Array<EngagementMapsRankedCardActionRowInfo> {
    const result: Array<EngagementMapsRankedCardActionRowInfo> = [];
    if (onSelectorNameElement) {
      result.push({
        title: intl.formatMessage({ defaultMessage: 'Name element', id: 'bH9Klu' }),
        action: rank => onSelectorNameElement(rank, true),
        icon: 'code',
        canAccess: flags.hasBit(Flag.PermModifyNamedElements),
      });
      if (selector.namedElementRecords.length) {
        result.push({
          title: intl.formatMessage({ defaultMessage: 'View existing element', id: 'VA7uX9' }),
          action: rank => onSelectorNameElement(rank, false),
          icon: 'code',
          canAccess: true,
        });
      }
    }
    if (onSelectorModifyRecordingRules) {
      result.push({
        title: intl.formatMessage({ defaultMessage: 'Create data capture rule', id: 'r+QQlx' }),
        action: onSelectorModifyRecordingRules,
        icon: 'detections',
        canAccess: flags.hasBit(Flag.PermModifyElementBlocks),
      });
    }
    if (onSelectorIgnoreInFrustrationSignals) {
      result.push({
        title: intl.formatMessage({
          defaultMessage: 'Ignore in frustration signals',
          id: 'FON/og',
        }),
        action: onSelectorIgnoreInFrustrationSignals,
        icon: 'rage',
        canAccess:
          flags.hasBit(Flag.PermModifyBehaviorSignalSettings) &&
          flags.hasBit(Flag.PermModifyNamedElements),
      });
    }
    return result;
  }

  const clickMapsTypeOptions: Array<OptionType<ClickTypes>> = clickTypes.map(
    (typeValue: ClickTypes) => createClickMapsTypeOption(typeValue),
  );

  const engagementMapsCard =
    segmentData && clickMapsData && clickMapsData.topSelectorData.length > 0 ? (
      <>
        <EngagementMapsDetailsCard
          name={intl.formatMessage({ defaultMessage: 'Click Map', id: 'LcQKiM' })}
        >
          <SegmentRow segmentName={segmentData.segment ?? null} onRowClick={navigateToSegment} />
          {segmentData.pageDefinition ? (
            <PageDefinitionRow
              pageDefinition={segmentData.pageDefinition}
              onPageEdit={() => history.push(generateRoutePath(SETTINGS_PAGES, { orgId }))}
              canAccess={canAccess}
            />
          ) : (
            <PagePathRow
              canonicalPagePath={segmentData.canonicalPagePath}
              domain={segmentData.pageDomain}
            />
          )}
          <TimeFrameRow
            timeFrame={segmentData.timeFrame}
            showTimeFrameWarning={segmentData.showTimeFrameWarning}
          />
          <ClicksRow clicks={clickMapsData.clicks} clickType={currentClickMapsType} />
          <UsersRow users={clickMapsData.clickTypeUsers} clickType={currentClickMapsType} />
          {flags.featureFlags().AppGlidePaths && (
            <>
              <Box paddingBottom={16}>
                <Divider variant="medium" />
              </Box>
              <Box testId="heatmap-explore-glidepath" paddingBottom={4}>
                <Flex justifyContent="space-between">
                  <Typography variant="body4">
                    {intl.formatMessage({
                      defaultMessage:
                        'Explore this click map with a different time range, segment, or device.',
                      id: 'grJ3Iv',
                    })}
                  </Typography>
                  <Box width="max-content">
                    <Button
                      testId="heatmap-explore-button"
                      size="x-small"
                      variant="fill-secondary"
                      onClick={() => routeToHeatmap(routeSettings)}
                    >
                      {intl.formatMessage({ defaultMessage: 'Explore', id: '7JlauX' })}
                    </Button>
                  </Box>
                </Flex>
              </Box>
            </>
          )}
        </EngagementMapsDetailsCard>
        <Flex>
          <Box paddingTop={16} paddingLeft={16} paddingBottom={8} paddingRight={16}>
            <Typography variant="body3" color="neutrals-n7" textAlign="center">
              <FormattedMessage
                defaultMessage="Tip: Quickly flip through top {currentClickMapsType, select, error_clicked {error clicked} rage_clicked {rage clicked} dead_clicked {dead clicked} other {clicked}} elements using the ↓ and ↑ keys."
                id="vV9Dyz"
                values={{ currentClickMapsType }}
              />
            </Typography>
          </Box>
        </Flex>
        <Pane paddingBottom={16}>
          {clickMapsData.topSelectorData.map((selector, i) => (
            <Pane
              paddingTop={8}
              paddingLeft={8}
              paddingRight={8}
              backgroundColor="secondary"
              key={selector.selector}
            >
              <EngagementMapsRankedCard
                rankIndicator={{
                  rank: i,
                  color: getClickTypeColor(currentClickMapsType),
                  size: 24,
                  active: i === activeSelector,
                }}
                selector={selector.selector}
                namedElementRecords={selector.namedElementRecords}
                facts={selector.facts}
                searchActions={createSearchActions(
                  flags,
                  onSelectorAddToSearch,
                  onSelectorCreateSearch,
                  onSelectorAddDefinedEvent,
                )}
                recordingActions={createActions(selector)}
                onClick={onSelectorClick ? onSelectorClick : noop}
                expanded={i === activeSelector}
              />
            </Pane>
          ))}
        </Pane>
      </>
    ) : clickMapsData ? (
      <Box padding={16}>
        <Typography variant="body3" color="neutrals-n8" textAlign="center">
          <FormattedMessage
            defaultMessage="{userCount, number} users have {currentClickMapsType, select, error_clicked {error clicked} rage_clicked {rage clicked} dead_clicked {dead clicked} other {clicked}} {clickCount, number} times on this page, but none of the {currentClickMapsType, select, error_clicked {error clicked} rage_clicked {rage clicked} dead_clicked {dead clicked} other {clicked}} elements are visible here."
            id="AXdCBZ"
            values={{
              userCount: clickMapsData.clickTypeUsers,
              clickCount: clickMapsData.clicks,
              currentClickMapsType,
            }}
          />
        </Typography>
      </Box>
    ) : null;

  return (
    <Box data-component="ClickMapsSidebar" data-fs="click-maps-sidebar">
      <Pane
        border="standard"
        backgroundColor="default"
        borderRadiusTopLeft={6}
        borderRadiusTopRight={6}
      >
        <Box padding={16}>
          <Box paddingBottom={8}>
            <Container verticalAlign="center">
              <Column>
                <Typography
                  variant="heading5"
                  textTransform="uppercase"
                  letterSpacing={0.75}
                  color="neutrals-n11"
                >
                  <FormattedMessage defaultMessage="Engagement Heatmap" id="Bt38pj" />
                </Typography>
              </Column>
              <Container align="end">
                <Box
                  data-fs="click-maps-sidebar-heatmap-toggle"
                  data-active={engagementHeatMapToggleChecked}
                >
                  <Toggle
                    checked={engagementHeatMapToggleChecked}
                    onToggle={onEngagementHeatMapToggle}
                  />
                </Box>
              </Container>
            </Container>
          </Box>
          <Box data-fs="click-maps-sidebar-click-type-menu" data-click-type={currentClickMapsType}>
            <SelectMenu
              size="large"
              variant="fill-builder"
              fullWidth
              onChange={onClickMapsTypeChange}
              options={clickMapsTypeOptions}
              selected={createClickMapsTypeOption(currentClickMapsType)}
            />
          </Box>
        </Box>
      </Pane>
      <Pane
        backgroundColor="secondary"
        border="standard"
        borderRadiusBottomLeft={6}
        borderRadiusBottomRight={6}
      >
        <EngagementMapsStatus
          name={intl.formatMessage({ defaultMessage: 'Click Maps', id: 'y9aCyB' })}
          state={loadingState}
          data={engagementMapsCard}
        />
      </Pane>
      <Box marginTop={16}>
        <ContactUs />
      </Box>
    </Box>
  );
};
